import type { FunctionComponent } from "react";
import React from "react";
import { i18nFilter } from "app/filters/i18nFilter";
import styled from "styled-components";
import { colorsPalettes, rgba } from "@similarweb/styles";
import WebsiteWithLink from "./website-with-link/WebsiteWithLink";
import { IconButton } from "@similarweb/ui-components";
import type { QualificationSideBarSelectionType } from "app/components/QualificationSideBar/types";
import { FaviconButtonContainer } from "./favIcon/FaviconButtonContainer";
import {
    CenteredFlexColumn,
    CenteredFlexRow,
    FlexColumn,
} from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { PlainTooltip } from "@similarweb/ui-components";
import { chosenSites } from "app/common/services/chosenSites";
import { allTrackers } from "app/services/track/track";

const HeaderBar = styled(CenteredFlexRow)`
    box-sizing: border-box;
    justify-content: space-between;
    font-size: 16px;
    color: ${colorsPalettes.carbon["0"]};
    padding: 24px 16px 24px 22px;
    font-weight: 500;
    line-height: 0.8;
    letter-spacing: normal;
`;
const MainHeaderBar = styled(HeaderBar)`
    height: 60px;
    background-color: ${colorsPalettes.carbon["400"]};
`;
const SecondaryHeaderBar = styled(HeaderBar)`
    height: 60px;
    padding: 24px 8px 24px 22px;
    background-color: ${colorsPalettes.carbon["0"]};
`;
const HeaderContainer = styled.div<{ isScrolling?: boolean }>`
    height: 120px;
    border-bottom: 1px solid ${colorsPalettes.carbon[100]};
    z-index: 10;
    ${({ isScrolling }) => isScrolling && `box-shadow: 0px 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};`};
`;
const StyledHeaderLeft = styled(FlexColumn)``;
const StyledHeaderRight = styled(FlexColumn)`
    flex-shrink: 0;
`;
const StyledCloseButton = styled(CenteredFlexColumn)`
    color: ${colorsPalettes.carbon["0"]};
    && svg,
    &&:hover svg {
        path {
            fill: ${colorsPalettes.carbon["0"]};
        }
    }
`;
const FaviconButtonStyle = styled.div`
    margin: 0 8px 0 4px;
`;

type HeaderProps = {
    duration: string;
    country: string;
    qualificationSideBarSelection: QualificationSideBarSelectionType;
    closeQualificationSideBar: () => void;
    isScrolling: boolean;
};

const hoverMessagesObject = {
    labelFavoriteAdd: "qualification.sidebar.header.button.favorites.website.label.add",
    labelFavoriteRemove: "qualification.sidebar.header.button.favorites.website.label.remove",
    hoverTitleFavoriteAdd: "qualification.sidebar.header.button.favorites.website.tooltip.add",
    hoverTitleFavoriteRemove: "qualification.sidebar.header.button.favorites.website.tooltip.remove",
    addSuccessToastMessage: "qualification.sidebar.header.button.favorites.website.add.confirmation",
    removeSuccessToastMessage: "qualification.sidebar.header.button.favorites.website.remove.confirmation",
    addErrorToastMessage: "qualification.sidebar.header.button.favorites.website.add.error",
    removeErrorToastMessage: "qualification.sidebar.header.button.favorites.website.remove.error",
};

const Header: FunctionComponent<HeaderProps> = ({
    duration,
    country,
    qualificationSideBarSelection,
    closeQualificationSideBar,
    isScrolling,
}) => {
    const i18n = i18nFilter();
    const website = qualificationSideBarSelection?.data;
    return (
        <HeaderContainer isScrolling={isScrolling}>
            <MainHeaderBar>
                <StyledHeaderLeft>{website && <WebsiteWithLink website={website} />}</StyledHeaderLeft>
                <StyledHeaderRight>
                    <PlainTooltip
                        placement="left"
                        tooltipContent={i18n("qualification.sidebar.header.button.close.tooltip")}
                    >
                        <StyledCloseButton>
                            <IconButton
                                iconSize="sm"
                                iconName="clear"
                                type="flat"
                                onClick={() => {
                                    allTrackers.dispatchTrackEvent("Expanded Side Bar", "close", "close sidebar");
                                    closeQualificationSideBar();
                                }}
                            />
                        </StyledCloseButton>
                    </PlainTooltip>
                </StyledHeaderRight>
            </MainHeaderBar>
            <SecondaryHeaderBar>
                <FaviconButtonStyle>
                    <FaviconButtonContainer
                        duration={duration}
                        country={country}
                        hoverMessages={hoverMessagesObject}
                        website={website?.domain}
                        trackEventName={
                            (chosenSites.get().length > 1 ? "Comparison" : "Site") +
                            "/Website Analysis/" +
                            chosenSites.get().join()
                        }
                    />
                </FaviconButtonStyle>
            </SecondaryHeaderBar>
        </HeaderContainer>
    );
};

export const MemoizedHeader = React.memo(Header);
